export type TableRouteState = {
  searchText: string;
  page: number;
  pageSize: number;
  filters: Record<string, string[]>;
};

/** Namespaced param key, e.g. `history.page`. */
export const routeParamKey = (prefix: string, key: string): string => `${prefix}.${key}`;

const positiveInt = (value: string | null, fallback: number): number => {
  const parsed = parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/** Reads the table's state from a URL query string; absent params fall back to the provided defaults (search/filters default to empty). */
export const readTableRouteState = (
  search: string,
  prefix: string,
  filterKeys: string[],
  defaults: { page: number; pageSize: number }
): TableRouteState => {
  const params = new URLSearchParams(search);
  const filters: Record<string, string[]> = {};
  filterKeys.forEach((key) => {
    const values = (params.get(routeParamKey(prefix, key)) ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    if (values.length) {
      filters[key] = values;
    }
  });
  return {
    searchText: params.get(routeParamKey(prefix, 'q')) ?? '',
    page: positiveInt(params.get(routeParamKey(prefix, 'page')), defaults.page),
    pageSize: positiveInt(params.get(routeParamKey(prefix, 'pageSize')), defaults.pageSize),
    filters,
  };
};

/** Returns a new URLSearchParams with this table's prefixed keys set (or deleted when empty/default); all other params are preserved. */
export const writeTableRouteParams = (
  current: URLSearchParams,
  prefix: string,
  state: TableRouteState,
  filterKeys: string[]
): URLSearchParams => {
  const next = new URLSearchParams(current);
  const setOrDelete = (key: string, value: string) => {
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
  };
  setOrDelete(routeParamKey(prefix, 'q'), state.searchText);
  setOrDelete(routeParamKey(prefix, 'page'), state.page > 1 ? String(state.page) : '');
  setOrDelete(routeParamKey(prefix, 'pageSize'), String(state.pageSize));
  filterKeys.forEach((key) => setOrDelete(routeParamKey(prefix, key), (state.filters[key] ?? []).join(',')));
  return next;
};
